import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import hrApi from '../utils/hrApi';

const t = {
  bg: '#f4f6f8', panel: '#ffffff', border: '#e3e8ee', text: '#1a2230', muted: '#6b7280',
  brand: '#0D9488', brandDark: '#0b7d72', red: '#dc2626', amber: '#d97706', green: '#16a34a',
};
const panel = { background: t.panel, border: `1px solid ${t.border}`, borderRadius: 12 };
const inputCss = { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', color: t.text, background: '#fff' };
const btn = { background: t.brand, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const ghost = { background: 'transparent', color: t.text, border: `1px solid ${t.border}`, borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

const SUGGESTIONS = [
  'How many annual leave days do I get?',
  'What is the sick leave policy?',
  'What are the standard working hours?',
  'How do I request leave?',
  'What are my details? (I will give my ID card)',
];

export default function HrAssistantPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [convos, setConvos] = useState([]);
  const [convId, setConvId] = useState(null);
  const [confirm, confirmNode] = useConfirm();
  const scroller = useRef(null);

  const loadStatus = useCallback(() => { hrApi.get('/status').then(setStatus).catch(e => setStatus({ error: true, denied: e?.error })); }, []);
  const loadConvos = useCallback(() => { hrApi.get('/conversations').then(setConvos).catch(() => setConvos([])); }, []);
  useEffect(() => { loadStatus(); loadConvos(); }, [loadStatus, loadConvos]);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [messages, busy]);

  const newChat = () => { setConvId(null); setMessages([]); setQ(''); };
  const openChat = async (id) => {
    try {
      const d = await hrApi.get(`/conversations/${id}`);
      setConvId(id);
      setMessages((d.messages || []).map(m => ({ role: m.role, text: m.content, ...(m.meta || {}) })));
    } catch (e) { /* ignore */ }
  };
  const deleteChat = async (id, e) => {
    e.stopPropagation();
    const ok = await confirm({ title: 'Delete conversation', message: 'This conversation will be permanently deleted.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try { await hrApi.delete(`/conversations/${id}`); if (id === convId) newChat(); loadConvos(); } catch (err) { /* ignore */ }
  };

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || busy) return;
    setQ('');
    const history = messages.map(m => ({ role: m.role, text: m.text }));
    setMessages(m => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const out = await hrApi.post('/ask', { question: text, history, conversation_id: convId });
      if (out.conversation_id && out.conversation_id !== convId) { setConvId(out.conversation_id); }
      setMessages(m => [...m, { role: 'assistant', text: out.answer, chunks: out.chunks || [], answered: out.answered, rateLimited: out.rateLimited, private: out.private, engine: out.engine, personal: out.personal }]);
      loadConvos();
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: e?.error || 'Something went wrong. Please try again.', error: true }]);
    } finally { setBusy(false); }
  };

  const docCount = status?.documents?.length || 0;
  const noManual = status && !status.error && docCount === 0;
  const notConfigured = status && !status.error && !status.configured;

  // Denied: not granted access (and not superadmin). Show a clear message.
  if (status && status.error && status.denied) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: t.bg, color: t.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: 24 }}>
        <div style={{ ...panel, padding: 32, maxWidth: 440, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8 }}>No access to HR Assistant</div>
          <div style={{ fontSize: 13.5, color: t.muted, marginBottom: 20 }}>{status.denied}</div>
          <button style={btn} onClick={() => navigate('/hub')}>← Back to App Hub</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: t.bg, color: t.text, display: 'flex', fontFamily: "'Inter', system-ui, sans-serif" }}>
      {confirmNode}
      {/* conversations sidebar */}
      <div style={{ width: 250, background: t.panel, borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
        <div style={{ padding: 12, borderBottom: `1px solid ${t.border}` }}>
          <button style={{ ...btn, width: '100%' }} onClick={newChat}>+ New chat</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
          {convos.length === 0 && <div style={{ color: t.muted, fontSize: 12, padding: 12, textAlign: 'center' }}>No saved chats yet.</div>}
          {convos.map(c => (
            <div key={c.id} onClick={() => openChat(c.id)} style={{
              padding: '9px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
              background: c.id === convId ? '#e6f7f4' : 'transparent', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title}</div>
                <div style={{ fontSize: 10, color: t.muted }}>{new Date(c.updated_at).toLocaleDateString()}</div>
              </div>
              <button onClick={(e) => deleteChat(c.id, e)} title="Delete" style={{ background: 'none', border: 'none', color: t.muted, cursor: 'pointer', fontSize: 14, padding: 2 }}>✕</button>
            </div>
          ))}
        </div>
        <div style={{ padding: 10, borderTop: `1px solid ${t.border}` }}>
          <button style={{ ...ghost, width: '100%' }} onClick={() => navigate('/hub')}>← App Hub</button>
        </div>
      </div>

      {/* main column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* header */}
      <div style={{ background: t.panel, borderBottom: `1px solid ${t.border}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: t.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800 }}>HR</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>HR Assistant</div>
          <div style={{ fontSize: 11, color: t.muted }}>{docCount > 0 ? `Answering from ${docCount} HR document${docCount===1?'':'s'}` : 'Company HR document Q&A'}</div>
        </div>
        {status?.is_manager && <button style={ghost} onClick={() => setShowAdmin(v => !v)}>{showAdmin ? 'Close' : 'Manage manual'}</button>}
        {status?.is_super && <button style={ghost} onClick={() => setShowAccess(true)}>Settings</button>}
      </div>

      {showAdmin && status?.is_manager && <AdminPanel status={status} onClose={() => setShowAdmin(false)} onDone={() => { loadStatus(); }} />}
      {showAccess && status?.is_super && <AccessModal onClose={() => setShowAccess(false)} />}

      {/* chat body */}
      <div ref={scroller} style={{ flex: 1, overflow: 'auto', padding: '20px 0' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 20px' }}>
          {notConfigured && !status?.local && status?.provider !== 'hybrid' && <Banner kind="warn">The AI isn't set up yet. A super admin needs to add an API key in Settings, or the server needs the local model.</Banner>}
          {status?.local && <Banner kind="ok">🔒 Private mode: answers are generated by a model running on your own server. Nothing leaves the company.</Banner>}
          {status?.provider === 'hybrid' && <Banner kind="ok">⚡ Fast mode: policy answers are fast; your personal details are always answered privately and never leave the server.</Banner>}
          {status?.provider === 'filesearch' && <Banner kind="ok">✨ Smart mode: policy answers use Google's managed document search over your manual, with citations. Personal details are always answered privately on our server.</Banner>}
          {status?.provider === 'claude' && <Banner kind="ok">✨ Premium mode: policy answers are generated by Claude reading your full HR manual in detail. Personal details are always answered privately on our server.</Banner>}
          {noManual && <Banner kind="warn">No HR documents have been uploaded yet.{status?.is_manager ? ' Use “Manage manual” above to upload it.' : ' Please ask an administrator to upload it.'}</Banner>}

          {messages.length === 0 && !noManual && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: t.muted }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 6 }}>Ask about company HR policy</div>
              <div style={{ fontSize: 13, marginBottom: 20 }}>Answers come only from the company HR documents — not from outside sources.</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {SUGGESTIONS.map(s => <button key={s} onClick={() => ask(s)} style={{ ...ghost, fontSize: 12.5 }}>{s}</button>)}
              </div>
            </div>
          )}

          {messages.map((m, i) => <Bubble key={i} m={m} />)}
          {busy && <Bubble m={{ role: 'assistant', text: '…', typing: true }} />}
        </div>
      </div>

      {/* input */}
      <div style={{ borderTop: `1px solid ${t.border}`, background: t.panel, padding: '14px 20px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', gap: 10 }}>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') ask(); }}
            placeholder="Ask a question about company HR policy…"
            disabled={busy || noManual} style={inputCss} />
          <button style={{ ...btn, opacity: busy || !q.trim() ? 0.6 : 1 }} disabled={busy || !q.trim()} onClick={() => ask()}>Ask</button>
        </div>
        <div style={{ maxWidth: 760, margin: '6px auto 0', fontSize: 10.5, color: t.muted, textAlign: 'center' }}>
          Answers are generated from your company HR documents and may not be exhaustive. For anything important, confirm with HR.
        </div>
      </div>
      </div>
    </div>
  );
}

// Lightweight markdown renderer for Claude's answers: headings, bold, bullet/number
// lists, and pipe-tables become properly formatted elements (no raw ## ** | symbols).
function renderInline(text, keyBase) {
  // bold **x**, then italic *x*
  const parts = [];
  let rest = String(text);
  let idx = 0;
  const re = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`/g;
  let last = 0, mm;
  while ((mm = re.exec(rest)) !== null) {
    if (mm.index > last) parts.push(rest.slice(last, mm.index));
    if (mm[1]) parts.push(<strong key={keyBase + '-b' + idx}>{mm[1]}</strong>);
    else if (mm[2]) parts.push(<em key={keyBase + '-i' + idx}>{mm[2]}</em>);
    else if (mm[3]) parts.push(<code key={keyBase + '-c' + idx} style={{ background: '#eef1f4', padding: '1px 4px', borderRadius: 3, fontSize: 12.5 }}>{mm[3]}</code>);
    last = mm.index + mm[0].length; idx++;
  }
  if (last < rest.length) parts.push(rest.slice(last));
  return parts;
}
function Markdown({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let i = 0, key = 0;
  while (i < lines.length) {
    let line = lines[i];
    // table: a line with | ... | followed by a separator row of ---
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:-]*\|[\s:|-]*$/.test(lines[i + 1])) {
      const header = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
        i++;
      }
      blocks.push(
        <div key={key++} style={{ overflowX: 'auto', margin: '8px 0' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <thead><tr>{header.map((h, x) => <th key={x} style={{ border: `1px solid ${t.border}`, padding: '6px 10px', background: '#f3f5f7', textAlign: 'left', fontWeight: 700 }}>{renderInline(h, 'th' + x)}</th>)}</tr></thead>
            <tbody>{rows.map((r, y) => <tr key={y}>{r.map((c, x) => <td key={x} style={{ border: `1px solid ${t.border}`, padding: '6px 10px' }}>{renderInline(c, 'td' + y + x)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    // headings
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const lvl = h[1].length;
      blocks.push(<div key={key++} style={{ fontWeight: 800, fontSize: lvl <= 2 ? 15 : 13.5, margin: '10px 0 4px' }}>{renderInline(h[2], 'h' + key)}</div>);
      i++; continue;
    }
    // bullet list
    if (/^\s*[-*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*•]\s+/, '')); i++; }
      blocks.push(<ul key={key++} style={{ margin: '4px 0', paddingLeft: 20 }}>{items.map((it, x) => <li key={x} style={{ marginBottom: 2 }}>{renderInline(it, 'li' + x)}</li>)}</ul>);
      continue;
    }
    // numbered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+[.)]\s+/, '')); i++; }
      blocks.push(<ol key={key++} style={{ margin: '4px 0', paddingLeft: 22 }}>{items.map((it, x) => <li key={x} style={{ marginBottom: 2 }}>{renderInline(it, 'ol' + x)}</li>)}</ol>);
      continue;
    }
    // horizontal rule
    if (/^\s*---+\s*$/.test(line)) { blocks.push(<hr key={key++} style={{ border: 'none', borderTop: `1px solid ${t.border}`, margin: '10px 0' }} />); i++; continue; }
    // blank line
    if (line.trim() === '') { blocks.push(<div key={key++} style={{ height: 6 }} />); i++; continue; }
    // paragraph (collect consecutive non-special lines)
    const para = [line]; i++;
    while (i < lines.length && lines[i].trim() !== '' && !/^\s*(#{1,6}\s|[-*•]\s|\d+[.)]\s|\|)/.test(lines[i]) && !/^\s*---+\s*$/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push(<div key={key++} style={{ margin: '2px 0' }}>{renderInline(para.join(' '), 'p' + key)}</div>);
  }
  return <div>{blocks}</div>;
}

function Bubble({ m }) {
  const isUser = m.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 14 }}>
      <div style={{
        maxWidth: isUser ? '82%' : '92%', padding: '11px 14px', borderRadius: 14, fontSize: 14, lineHeight: 1.55,
        background: isUser ? t.brand : (m.error ? '#fef2f2' : t.panel),
        color: isUser ? '#fff' : (m.error ? t.red : t.text),
        border: isUser ? 'none' : `1px solid ${t.border}`,
        borderBottomRightRadius: isUser ? 4 : 14, borderBottomLeftRadius: isUser ? 14 : 4,
      }}>
        {m.typing ? <span style={{ color: t.muted }}>Thinking…</span> : (isUser ? <span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span> : <Markdown text={m.text} />)}
        {!isUser && !m.typing && (m.private !== undefined || m.personal) && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 700 }}>
            {m.personal
              ? <span style={{ color: t.green, display: 'inline-flex', alignItems: 'center', gap: 4 }}>🔒 Your personal details — answered privately</span>
              : <span style={{ color: t.muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>📘 From the company HR documents</span>}
          </div>
        )}
        {!isUser && m.chunks && m.chunks.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${t.border}`, fontSize: 11, color: t.muted }}>
            {(() => {
              const headings = [...new Set(m.chunks.map(c => c.heading).filter(Boolean))].slice(0, 3);
              const docs = [...new Set(m.chunks.map(c => c.doc_title).filter(Boolean))].slice(0, 2);
              if (headings.length) return <span>Source: {docs.join(', ') || 'HR document'} — {headings.join(' · ')}</span>;
              return <span>Source: {docs.join(', ') || 'HR document'}</span>;
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

// In-app confirmation dialog (replaces the browser's window.confirm) — used app-wide.
function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95, padding: 20 }}>
      <div style={{ ...panel, width: 420, maxWidth: '100%', padding: 22 }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{title || 'Are you sure?'}</div>
        <div style={{ fontSize: 13.5, color: t.muted, marginBottom: 20, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={ghost} onClick={onCancel}>Cancel</button>
          <button style={{ ...btn, background: danger ? t.red : t.brand }} onClick={onConfirm}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}
// Hook: const confirm = useConfirm(); then  confirm({ title, message, danger }).then(ok => { if (ok) ... })
function useConfirm() {
  const [state, setState] = React.useState(null);
  const confirm = React.useCallback((opts) => new Promise(resolve => { setState({ ...opts, resolve }); }), []);
  const node = state ? (
    <ConfirmDialog open title={state.title} message={state.message} confirmLabel={state.confirmLabel} danger={state.danger}
      onConfirm={() => { state.resolve(true); setState(null); }}
      onCancel={() => { state.resolve(false); setState(null); }} />
  ) : null;
  return [confirm, node];
}


function Banner({ kind, children }) {
  const c = kind === 'warn' ? t.amber : t.brand;
  return <div style={{ ...panel, borderLeft: `4px solid ${c}`, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: t.text }}>{children}</div>;
}

function AdminPanel({ status, onClose, onDone }) {
  const [confirm, confirmNode] = useConfirm();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('manual');
  const [priority, setPriority] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [gaps, setGaps] = useState([]);
  const [health, setHealth] = useState(null);
  const [docs, setDocs] = useState([]);
  const [empFile, setEmpFile] = useState(null);
  const [empMsg, setEmpMsg] = useState('');
  const [empBusy, setEmpBusy] = useState(false);
  const [embedBusy, setEmbedBusy] = useState(false);
  const [embedMsg, setEmbedMsg] = useState('');
  const buildEmbeddings = async () => {
    setEmbedBusy(true); setEmbedMsg('Building smart search… this can take a minute.');
    try {
      const r = await hrApi.post('/embed');
      if (r.noKey) setEmbedMsg('Add an API key first (Settings → API keys), then build again.');
      else setEmbedMsg(`Smart search ready — indexed ${r.embedded} of ${r.total} sections.`);
      onDone();
    } catch (e) { setEmbedMsg(e?.error || 'Failed.'); } finally { setEmbedBusy(false); }
  };

  const loadDocs = useCallback(() => { hrApi.get('/documents').then(setDocs).catch(() => setDocs([])); }, []);
  useEffect(() => { hrApi.get('/gaps').then(setGaps).catch(() => setGaps([])); loadDocs(); }, [loadDocs]);
  const checkHealth = useCallback(() => { setHealth('checking'); hrApi.get('/health').then(setHealth).catch(e => setHealth({ ok: false, message: e?.error || 'Check failed.' })); }, []);
  useEffect(() => { checkHealth(); }, [checkHealth]);

  const upload = async () => {
    if (!file) { setMsg('Choose a PDF, Word or text file first.'); return; }
    setBusy(true); setMsg('Reading and indexing the document…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (title.trim()) fd.append('title', title.trim());
      fd.append('doc_type', docType);
      if (priority !== '') fd.append('priority', priority);
      if (effectiveDate) fd.append('effective_date', effectiveDate);
      const out = await hrApi.post('/manual', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg(`Loaded “${out.document.title}” (${out.document.doc_type}) — ${out.document.chunk_count} sections indexed.`);
      setFile(null); setTitle(''); setEffectiveDate('');
      loadDocs(); onDone();
    } catch (e) { setMsg(e?.error || 'Upload failed.'); } finally { setBusy(false); }
  };
  const removeDoc = async (d) => {
    const ok = await confirm({ title: 'Remove document', message: `“${d.title}” will no longer be used for answers.`, confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await hrApi.delete(`/documents/${d.id}`); loadDocs(); onDone(); } catch (e) { alert(e?.error || 'Failed'); }
  };
  const typeLabel = { manual: 'Base manual', policy: 'Policy (overrides manual)', amendment: 'Amendment (overrides manual)', guideline: 'Guideline (adds detail)' };
  const uploadEmployees = async () => {
    if (!empFile) { setEmpMsg('Choose an Excel or CSV file first.'); return; }
    setEmpBusy(true); setEmpMsg('Reading employee records…');
    try {
      const fd = new FormData(); fd.append('file', empFile);
      const out = await hrApi.post('/employees', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEmpMsg(`Loaded ${out.count} employee records. Salary and grade columns were ignored.`);
      setEmpFile(null); onDone();
    } catch (e) { setEmpMsg(e?.error || 'Upload failed.'); } finally { setEmpBusy(false); }
  };
  const clearCache = async () => {
    const ok = await confirm({ title: 'Clear saved answers', message: 'The next questions will be generated fresh from the current documents.', confirmLabel: 'Clear' });
    if (!ok) return;
    try { await hrApi.post('/cache/clear'); onDone(); } catch (e) { alert(e?.error || 'Failed'); }
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 75, padding: '4vh 20px' }}>
      {confirmNode}
      <div style={{ ...panel, width: 960, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 22px', borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 800, flex: 1 }}>Manage documents & records</div>
          <button style={ghost} onClick={onClose}>Close</button>
        </div>
        <div style={{ overflow: 'auto', padding: '18px 22px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Add an HR document</div>
          <div style={{ fontSize: 12, color: t.muted, marginBottom: 10 }}>
            Add the base manual plus any newer policies, amendments or guidelines. When they conflict, a higher-priority document (policy/amendment) overrides the manual; guidelines add detail. PDF, Word (.docx) or text — the PDF must have a real text layer (not a scan).
          </div>
          <input placeholder="Title (e.g. SCT HR Manual 2026, or Leave Policy 2026)" value={title} onChange={e => setTitle(e.target.value)} style={{ ...inputCss, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={docType} onChange={e => setDocType(e.target.value)} style={{ ...inputCss, flex: 1 }}>
              <option value="manual">Base manual</option>
              <option value="policy">Policy (overrides manual)</option>
              <option value="amendment">Amendment (overrides manual)</option>
              <option value="guideline">Guideline (adds detail)</option>
            </select>
            <select value={priority} onChange={e => setPriority(e.target.value)} title="Priority: higher wins on conflict" style={{ ...inputCss, width: 150 }}>
              <option value="">Priority: auto</option>
              <option value="0">Priority 0 (lowest)</option>
              <option value="1">Priority 1</option>
              <option value="2">Priority 2</option>
              <option value="3">Priority 3 (highest)</option>
            </select>
            <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} title="Effective date (optional)" style={{ ...inputCss, flex: 1 }} />
          </div>
          <input type="file" accept=".pdf,.docx,.txt" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13, marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={upload}>{busy ? 'Working…' : 'Add document'}</button>
            {msg && <span style={{ fontSize: 12, color: msg.includes('failed') || msg.includes('Choose') ? t.red : t.green }}>{msg}</span>}
          </div>

          {/* loaded documents */}
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Loaded documents ({docs.length})</div>
            {docs.length === 0 && <div style={{ fontSize: 12, color: t.muted }}>None yet.</div>}
            {docs.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: `1px solid ${t.border}`, fontSize: 12.5 }}>
                <span style={{ flex: 1 }}>
                  <b>{d.title}</b>
                  <span style={{ color: t.muted }}> · {typeLabel[d.doc_type] || d.doc_type} · priority {d.priority}{d.effective_date ? ` · from ${String(d.effective_date).slice(0, 10)}` : ''} · {d.chunk_count} sections</span>
                </span>
                <button style={{ ...ghost, padding: '3px 8px', fontSize: 11, color: t.red, borderColor: '#f3c2c2' }} onClick={() => removeDoc(d)}>Remove</button>
              </div>
            ))}
            <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Higher priority wins on conflict. To update a document, add the new version and remove the old one.</div>
          </div>
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#fff', border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Smart search (semantic)</div>
            <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>
              Understands the meaning of questions, not just keywords — so it finds the right section even when the wording differs. Needs an API key.
              {status.embedding && <div style={{ marginTop: 4 }}>Indexed: <b>{status.embedding.done}</b> / {status.embedding.total} sections{status.embedding.total > 0 && status.embedding.done >= status.embedding.total ? ' ✓' : ''}.</div>}
            </div>
            <button style={ghost} onClick={buildEmbeddings} disabled={embedBusy}>{embedBusy ? 'Building…' : 'Build / refresh smart search'}</button>
            {embedMsg && <div style={{ fontSize: 12, marginTop: 6, color: embedMsg.includes('key') ? t.amber : t.green }}>{embedMsg}</div>}
          </div>
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#fff', border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Saved answers (cache)</div>
            <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>
              Common policy answers are saved and reused instantly — this saves time and free-tier usage, and keeps repeat questions fully private. Personal answers are never cached. The cache clears automatically whenever you upload or remove a document.
              {status.cache && <div style={{ marginTop: 4 }}><b>{status.cache.entries}</b> saved · reused <b>{status.cache.served}</b> times.</div>}
            </div>
            <button style={ghost} onClick={clearCache}>Clear saved answers</button>
          </div>
          <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: '#fff', border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              AI service
              <button style={{ ...ghost, padding: '2px 8px', fontSize: 11 }} onClick={checkHealth}>Re-check</button>
            </div>
            {health === 'checking' && <div style={{ fontSize: 12, color: t.muted }}>Checking…</div>}
            {health && health !== 'checking' && (
              <div style={{ fontSize: 12, color: health.ok ? t.green : t.amber }}>
                {health.ok ? '✓ ' : '⚠ '}{health.message}
                <div style={{ color: t.muted, marginTop: 3 }}>Provider: {health.provider === 'ollama' ? 'Local (Ollama), on your server' : 'Google Gemini'} · Model: {health.model}</div>
              </div>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Employee information file</div>
          <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>
            Upload an Excel or CSV with one row per employee and a column for the ID card / employee number (plus start date, annual leave, department, etc.). Staff can then ask about their OWN details by giving their ID card — they can only ever see their own row. Salary and grade columns are ignored automatically.
            {typeof status.employee_count === 'number' && <div style={{ marginTop: 4 }}>Currently loaded: <b>{status.employee_count}</b> records.</div>}
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setEmpFile(e.target.files[0])} style={{ fontSize: 13, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
            <button style={{ ...btn, opacity: empBusy ? 0.6 : 1 }} disabled={empBusy} onClick={uploadEmployees}>{empBusy ? 'Working…' : 'Upload employees'}</button>
            {empMsg && <span style={{ fontSize: 12, color: empMsg.includes('failed') || empMsg.includes('Choose') ? t.red : t.green }}>{empMsg}</span>}
          </div>

          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Questions the manual didn't cover</div>
          <div style={{ ...panel, maxHeight: 180, overflow: 'auto' }}>
            {gaps.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: t.muted }}>Nothing yet — this fills in as people ask things the manual doesn't answer.</div>}
            {gaps.map((g, i) => (
              <div key={i} style={{ padding: '8px 12px', borderBottom: `1px solid ${t.border}`, fontSize: 12.5, display: 'flex', gap: 8 }}>
                <span style={{ flex: 1 }}>{g.question}</span>
                <span style={{ color: t.muted }}>×{g.times}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Use this to see what to add to the manual next.</div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}

function AccessModal({ onClose }) {
  const [tab, setTab] = useState('access');
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    setErr('');
    hrApi.get('/access').then(rows => setUsers(Array.isArray(rows) ? rows : [])).catch(e => { setUsers([]); setErr(e?.error || 'Could not load the user list.'); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const setLevel = async (u, level) => {
    setBusy(u.id);
    try { await hrApi.put(`/access/${u.id}`, { level }); await load(); }
    catch (e) { alert(e?.error || 'Failed'); } finally { setBusy(null); }
  };
  const visible = (users || []).filter(u => !q || `${u.full_name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80, padding: 20 }}>
      <div style={{ ...panel, width: 640, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${t.border}`, gap: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 800, flex: 1 }}>HR Assistant settings</div>
          <button style={ghost} onClick={onClose}>Close</button>
        </div>
        <div style={{ display: 'flex', gap: 4, padding: '8px 16px 0' }}>
          {[['access', 'Access'], ['keys', 'Gemini keys'], ['engine', 'AI Engine']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              background: 'none', border: 'none', borderBottom: `2px solid ${tab === k ? t.brand : 'transparent'}`,
              color: tab === k ? t.brand : t.muted, fontWeight: 700, fontSize: 13, padding: '8px 12px', cursor: 'pointer',
            }}>{l}</button>
          ))}
        </div>
        {tab === 'keys' ? <KeysPanel /> : tab === 'engine' ? <EnginePanel /> : (
        <>
        <div style={{ padding: '10px 20px' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search people…" style={inputCss} />
          <div style={{ fontSize: 11.5, color: t.muted, marginTop: 6 }}>Grant "Can ask questions" or "Manager" (manager can also upload documents). Nobody has access until you grant it.</div>
        </div>
        <div style={{ overflow: 'auto', padding: '0 20px 16px' }}>
          {!users && <div style={{ color: t.muted, padding: 16 }}>Loading…</div>}
          {err && <div style={{ color: t.red, padding: 16, fontSize: 13 }}>{err}</div>}
          {users && users.length === 0 && !err && <div style={{ color: t.muted, padding: 16, fontSize: 13 }}>No users found.</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {visible.map(u => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '9px 4px', fontWeight: 600 }}>{u.full_name}{u.is_super && <span style={{ fontSize: 10, color: t.brand, marginLeft: 6 }}>Super</span>}<div style={{ fontSize: 11, color: t.muted, fontWeight: 400 }}>{u.email}</div></td>
                  <td style={{ padding: '9px 4px', textAlign: 'right' }}>
                    {u.is_super ? <span style={{ color: t.green, fontWeight: 700, fontSize: 12 }}>Manager</span> :
                      <select disabled={busy === u.id} value={u.level} onChange={e => setLevel(u, e.target.value)} style={{ ...inputCss, width: 'auto', padding: '6px 10px', fontSize: 12.5 }}>
                        <option value="none">No access</option>
                        <option value="user">Can ask questions</option>
                        <option value="manager">Manager (ask + manage documents)</option>
                      </select>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function EnginePanel() {
  const [status, setStatus] = useState(null);
  const [claude, setClaude] = useState(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    hrApi.get('/status').then(s => { setStatus(s); }).catch(() => {});
    hrApi.get('/claude/status').then(c => { setClaude(c); if (c.model) setModel(c.model); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setProvider = async (p) => {
    setBusy(true); setMsg('');
    try { await hrApi.put('/provider', { provider: p }); setMsg('Engine switched to ' + label(p) + '.'); load(); }
    catch (e) { setMsg(e?.error || 'Failed'); } finally { setBusy(false); }
  };
  const saveClaudeKey = async () => {
    if (!keyDraft.trim()) { setMsg('Paste your Claude API key first.'); return; }
    setBusy(true); setMsg('Saving & testing…');
    try {
      const r = await hrApi.put('/claude/key', { api_key: keyDraft.trim(), model });
      setMsg(r.test?.ok ? '✓ Key saved and working.' : `Saved, but: ${r.test?.reason || 'test failed'}`);
      setKeyDraft(''); load();
    } catch (e) { setMsg(e?.error || 'Failed'); } finally { setBusy(false); }
  };
  const testClaude = async () => { setBusy(true); setMsg('Testing…'); try { const r = await hrApi.post('/claude/test'); setMsg(r.ok ? '✓ Claude is working.' : r.reason); } catch (e) { setMsg(e?.error || 'Failed'); } finally { setBusy(false); } };
  const label = (p) => ({ ollama: 'Local (free)', hybrid: 'Gemini + local (free)', gemini: 'Gemini', filesearch: 'Google File Search', claude: 'Claude (premium)' }[p] || p);

  const current = status?.provider || 'ollama';
  const opts = [
    ['ollama', 'Local — free, private, on your server (slower)'],
    ['hybrid', 'Gemini + local — free, fast policy answers'],
    ['claude', 'Claude — premium, best quality (paid API)'],
  ];
  return (
    <div style={{ padding: '12px 20px 20px', overflow: 'auto' }}>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Answering engine</div>
      <div style={{ fontSize: 12.5, color: t.muted, marginBottom: 12 }}>Choose which AI answers policy questions. Your staff's personal details are always answered locally and never sent out.</div>
      {opts.map(([k, d]) => (
        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${current === k ? t.brand : t.border}`, borderRadius: 8, marginBottom: 8, cursor: 'pointer', background: current === k ? '#f0fdfa' : '#fff' }}>
          <input type="radio" checked={current === k} onChange={() => setProvider(k)} disabled={busy} />
          <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 13 }}>{label(k)}</div><div style={{ fontSize: 11.5, color: t.muted }}>{d}</div></div>
          {current === k && <span style={{ fontSize: 10, color: t.green, fontWeight: 800 }}>ACTIVE</span>}
        </label>
      ))}

      {current === 'claude' && (
        <div style={{ ...panel, padding: 14, marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Claude API key</div>
          <div style={{ fontSize: 11.5, color: t.muted, marginBottom: 10 }}>
            Get a key at console.anthropic.com (add billing credits there). Paste it here — it's stored on your server, shown masked.
            {claude?.hasKey && <span> Current: <span style={{ fontFamily: 'monospace' }}>{claude.maskedKey}</span> ({claude.source})</span>}
          </div>
          <input type="password" placeholder="sk-ant-…" value={keyDraft} onChange={e => setKeyDraft(e.target.value)} style={{ ...inputCss, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={model} onChange={e => setModel(e.target.value)} style={{ ...inputCss, width: 'auto' }}>
              <option value="claude-haiku-4-5">Haiku (cheapest — good for lookups)</option>
              <option value="claude-sonnet-4-6">Sonnet (recommended)</option>
              <option value="claude-opus-4-6">Opus (max quality)</option>
            </select>
            <button style={btn} onClick={saveClaudeKey} disabled={busy}>Save & test</button>
            {claude?.hasKey && <button style={ghost} onClick={testClaude} disabled={busy}>Test</button>}
          </div>
          {!claude?.hasManual && <div style={{ fontSize: 11.5, color: t.amber, marginTop: 8 }}>Upload the HR manual (Manage manual) so Claude has something to read.</div>}
          <div style={{ fontSize: 11, color: t.muted, marginTop: 10, lineHeight: 1.5 }}>
            💡 To keep cost low: this mode now sends Claude only the RELEVANT sections of your manual (not the whole thing) per question, and reuses saved answers. Haiku is ~3× cheaper than Sonnet and fine for policy lookups. Repeat questions are free (served from cache).
          </div>
        </div>
      )}
      {msg && <div style={{ fontSize: 12.5, marginTop: 10, color: /✓|working|switched|saved/i.test(msg) ? t.green : (/saving|testing/i.test(msg) ? t.muted : t.red) }}>{msg}</div>}
    </div>
  );
}

function KeysPanel() {
  const [confirm, confirmNode] = useConfirm();
  const [keys, setKeys] = useState(null);
  const [drafts, setDrafts] = useState({ 1: '', 2: '', 3: '' });
  const [msg, setMsg] = useState({});
  const load = useCallback(() => { hrApi.get('/keys').then(d => setKeys(d.keys || [])).catch(() => setKeys([])); }, []);
  useEffect(() => { load(); }, [load]);
  const bySlot = {}; (keys || []).forEach(k => { bySlot[k.slot] = k; });

  const save = async (slot) => {
    const key = (drafts[slot] || '').trim();
    if (!key) { setMsg(m => ({ ...m, [slot]: 'Paste a key first.' })); return; }
    setMsg(m => ({ ...m, [slot]: 'Saving & testing…' }));
    try {
      await hrApi.put(`/keys/${slot}`, { api_key: key });
      const test = await hrApi.post(`/keys/${slot}/test`, {});
      setMsg(m => ({ ...m, [slot]: test.ok ? '✓ Saved and working.' : `Saved, but: ${test.reason}` }));
      setDrafts(d => ({ ...d, [slot]: '' })); load();
    } catch (e) { setMsg(m => ({ ...m, [slot]: e?.error || 'Failed.' })); }
  };
  const test = async (slot) => {
    setMsg(m => ({ ...m, [slot]: 'Testing…' }));
    try { const r = await hrApi.post(`/keys/${slot}/test`, {}); setMsg(m => ({ ...m, [slot]: r.ok ? '✓ Working.' : r.reason })); }
    catch (e) { setMsg(m => ({ ...m, [slot]: e?.error || 'Failed.' })); }
  };
  const del = async (slot) => { const ok = await confirm({ title: 'Remove key', message: 'This API key will be removed.', confirmLabel: 'Remove', danger: true }); if (!ok) return; try { await hrApi.delete(`/keys/${slot}`); load(); } catch (e) { /* ignore */ } };

  return (
    <div style={{ padding: '12px 20px 18px', overflow: 'auto' }}>
      {confirmNode}
      <div style={{ fontSize: 12.5, color: t.muted, marginBottom: 12 }}>
        Add up to three Google Gemini API keys. Questions are spread across the keys (each person stays on one key), and a key that hits its daily limit is skipped automatically. Get keys free at aistudio.google.com. Keys are stored on your server and shown masked.
      </div>
      {[1, 2, 3].map(slot => {
        const k = bySlot[slot];
        return (
          <div key={slot} style={{ ...panel, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Key {slot}</div>
              {k && <span style={{ fontSize: 11, color: t.muted, fontFamily: 'monospace' }}>{k.masked}</span>}
              {k && k.exhausted && <span style={{ fontSize: 10, color: t.amber, fontWeight: 700 }}>daily limit reached</span>}
              {k && !k.exhausted && <span style={{ fontSize: 10, color: t.green, fontWeight: 700 }}>active</span>}
              <div style={{ flex: 1 }} />
              {k && <button style={{ ...ghost, padding: '3px 8px', fontSize: 11 }} onClick={() => test(slot)}>Test</button>}
              {k && <button style={{ ...ghost, padding: '3px 8px', fontSize: 11, color: t.red }} onClick={() => del(slot)}>Remove</button>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="password" placeholder={k ? 'Replace key…' : 'Paste API key (AIza…)'} value={drafts[slot]} onChange={e => setDrafts(d => ({ ...d, [slot]: e.target.value }))} style={{ ...inputCss, flex: 1 }} />
              <button style={btn} onClick={() => save(slot)}>Save & test</button>
            </div>
            {msg[slot] && <div style={{ fontSize: 12, marginTop: 6, color: /working|saved and working|✓/i.test(msg[slot]) ? t.green : (/saving|testing/i.test(msg[slot]) ? t.muted : t.red) }}>{msg[slot]}</div>}
          </div>
        );
      })}
    </div>
  );
}
